import { createRouter, createWebHistory } from 'vue-router'

const HomeView = () => import('@/views/HomeView.vue')
const LoginView = () => import('@/views/LoginView.vue')
const RegisterView = () => import('@/views/RegisterView.vue')
const SetupView = () => import('@/views/SetupView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const UsersView = () => import('@/views/UsersView.vue')
const AuthenticationSettingsView = () => import('@/views/AuthenticationSettingsView.vue')
const StorageSettingsView = () => import('@/views/StorageSettingsView.vue')
const LibraryView = () => import('@/views/LibraryView.vue')
const SearchView = () => import('@/views/SearchView.vue')
const TagsView = () => import('@/views/TagsView.vue')
const TagDetailView = () => import('@/views/TagDetailView.vue')
const NewFacesView = () => import('@/views/NewFacesView.vue')
const AlbumsView = () => import('@/views/AlbumsView.vue')
const AlbumDetailView = () => import('@/views/AlbumDetailView.vue')
const MosaicsView = () => import('@/views/MosaicsView.vue')
const MosaicDetailView = () => import('@/views/MosaicDetailView.vue')
const FriendsView = () => import('@/views/FriendsView.vue')
const RunnersView = () => import('@/views/RunnersView.vue')
const RunnerDetailView = () => import('@/views/RunnerDetailView.vue')
const SharedWithMeView = () => import('@/views/SharedWithMeView.vue')
const SharedByMeView = () => import('@/views/SharedByMeView.vue')
const SharedAlbumView = () => import('@/views/SharedAlbumView.vue')
const TransferClaimView = () => import('@/views/TransferClaimView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')
const RemoteView = () => import('@/views/RemoteView.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/', name: 'home', component: HomeView },
    // `/remote/<id>` addresses one presentation; the bare form asks for the id instead.
    { path: '/remote/:presentationId?', name: 'remote', component: RemoteView, meta: { bare: true } },
    { path: '/login', name: 'login', component: LoginView },
    { path: '/register', name: 'register', component: RegisterView },
    { path: '/setup', name: 'setup', component: SetupView },
    { path: '/profile', name: 'profile', component: ProfileView, meta: { requiresAuth: true } },
    { path: '/library', name: 'library', component: LibraryView, meta: { requiresAuth: true } },
    {
      path: '/library/duplicates',
      name: 'duplicates',
      // Lazy: a cleanup screen most people open once, if ever — it should not be part of
      // the bundle everyone downloads to look at their photos.
      component: () => import('@/views/DuplicatesView.vue'),
      meta: { requiresAuth: true },
    },
    { path: '/search', name: 'search', component: SearchView, meta: { requiresAuth: true } },
    { path: '/tags', name: 'tags', component: TagsView, meta: { requiresAuth: true } },
    { path: '/tags/:id', name: 'tag-detail', component: TagDetailView, meta: { requiresAuth: true } },
    { path: '/faces', name: 'new-faces', component: NewFacesView, meta: { requiresAuth: true } },
    { path: '/albums', name: 'albums', component: AlbumsView, meta: { requiresAuth: true } },
    { path: '/mosaics', name: 'mosaics', component: MosaicsView, meta: { requiresAuth: true } },
    { path: '/friends', name: 'friends', component: FriendsView, meta: { requiresAuth: true } },
    { path: '/runners', name: 'runners', component: RunnersView, meta: { requiresAuth: true } },
    {
      path: '/runners/:id',
      name: 'runner-detail',
      component: RunnerDetailView,
      meta: { requiresAuth: true },
    },
    {
      path: '/shared',
      name: 'shared-with-me',
      component: SharedWithMeView,
      meta: { requiresAuth: true },
    },
    {
      path: '/shares',
      name: 'shared-by-me',
      component: SharedByMeView,
      meta: { requiresAuth: true },
    },
    {
      path: '/shared/:albumId',
      name: 'shared-album',
      component: SharedAlbumView,
      meta: { requiresAuth: true },
    },
    // Public link share: anyone with the link (the key is in its fragment) can view.
    { path: '/s/:token', name: 'shared-link', component: SharedAlbumView },
    // Watch along: the link carries a relay capability and no key at all, so this route
    // shows nothing until the presenter approves the device and seals it a grant.
    { path: '/watch/:presentationId?', name: 'watch', component: SharedAlbumView },
    {
      path: '/albums/:id',
      name: 'album-detail',
      component: AlbumDetailView,
      meta: { requiresAuth: true },
    },
    {
      path: '/mosaics/:id',
      name: 'mosaic-detail',
      component: MosaicDetailView,
      meta: { requiresAuth: true },
    },
    {
      path: '/users',
      name: 'users',
      component: UsersView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/authentication',
      name: 'authentication-settings',
      component: AuthenticationSettingsView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/storage',
      name: 'storage-settings',
      component: StorageSettingsView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      // Where a transfer claim link lands. Behind the normal session guard: the user
      // signs in to *this* instance however it authenticates, then approves.
      path: '/transfer/claim',
      name: 'transfer-claim',
      component: TransferClaimView,
      meta: { requiresAuth: true },
    },
    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundView },
  ],
})

router.beforeEach(async (to) => {
  if (to.meta.bare) return true
  const { useAuthStore } = await import('@/store/auth')
  const auth = useAuthStore()
  await auth.checkAuthConfig()

  if (auth.setupRequired && to.name !== 'setup') {
    return { name: 'setup' }
  }
  if (to.name === 'setup' && !auth.setupRequired) {
    return { name: 'home' }
  }
  if (auth.isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    return { name: 'home' }
  }
  if (to.name === 'register' && (!auth.authEnabled || !auth.userRegistrationEnabled)) {
    return { name: 'home' }
  }
  if (to.meta.requiresAuth && auth.authEnabled && !auth.isAuthenticated) {
    // Carry where they were going, so signing in (password or SSO) lands them back
    // there with the query intact — an account-transfer claim link depends on it.
    return { name: 'login', query: { return_to: to.fullPath } }
  }
  if (to.meta.requiresAdmin && auth.isAuthenticated && !auth.isAdmin) {
    return { name: 'home' }
  }
  return true
})

export default router
